import { Button, Divider, Form, Select, Space, Typography } from "antd";
import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useOnFormClose } from "@/hooks/forms";
import { useBatch } from "@/hooks/queries";
import { validationErrorParser } from "@/modules/form-validation";
import { getSelectMultipleValues } from "@/modules/select";
import { SidePanelFooter } from "@/modules/side-panel";
import UsersSelect from "@/modules/users/users-select";
import { Alert } from "@/ui/alert";

const { useForm } = Form;
// TODO DEV-8551 `Option` is deprecated. Please use `options` instead.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const { Option } = Select;

const BatchEdit = ({ models }) => {
  const [form] = useForm();
  const { t } = useTranslation();

  const [selectedProperty, setSelectedProperty] = useState("");

  const mutation = useBatch("projects", "update");
  const onClose = useOnFormClose(form);

  // Used for translation
  const projectCount = models.length;

  /**
   * Returns validation errors dictionnary.
   *
   * @return {Object}
   */
  function validationErrors() {
    return mutation.isError
      ? validationErrorParser(mutation.error, ["users"], "others")
      : {};
  }

  /**
   * Attempts to update projects
   *
   * @return {void}
   */
  // TODO IMP-1217 fix require-await
  // eslint-disable-next-line require-await
  async function onUpdate() {
    const data = {};
    data["projects"] = models.map((obj) => obj.id);
    if (selectedProperty === "users") {
      data["users"] = getSelectMultipleValues(form, "users", []);
    }

    mutation.mutate(data, {
      onSuccess: function () {
        onClose();
      },
    });
  }

  const FORM_ID = "batchEditProjectForm";

  return (
    <Form id={FORM_ID} form={form} layout="vertical" onFinish={onUpdate}>
      <Typography.Paragraph>
        {t("project:batchEdit.confirmation", {
          count: projectCount,
        })}
      </Typography.Paragraph>
      <Divider />
      <Form.Item
        name="properties"
        label={t("project:batchEdit.properties.label")}
      >
        <Select
          placeholder={t("project:batchEdit.properties.label")}
          onChange={(value) => setSelectedProperty(value)}
          data-testid="propertiesSelect"
        >
          <Option value="users" data-testid="usersOption">
            {t("project:batchEdit.userPlural.label")}
          </Option>
        </Select>
      </Form.Item>
      {selectedProperty && (
        <>
          {selectedProperty === "users" && (
            <UsersSelect
              label={t("project:form.users.label")}
              placeholder={t("project:form.users.label")}
              help={validationErrors().users}
            />
          )}
        </>
      )}
      {validationErrors().others && (
        <Form.Item
          validateStatus={validationErrors().others ? "error" : "success"}
          help={validationErrors().others}
        />
      )}
      <Alert
        display={mutation.isError}
        closable={{ afterClose: () => mutation.reset() }}
      />
      <SidePanelFooter>
        <Space>
          <Button onClick={onClose}>{t("global:cancel")}</Button>
          <Button
            loading={mutation.isPending}
            type="primary"
            data-testid="batchUpdate"
            htmlType="submit"
            form={FORM_ID}
          >
            {t("global:update")}
          </Button>
        </Space>
      </SidePanelFooter>
    </Form>
  );
};

BatchEdit.propTypes = {
  models: PropTypes.array.isRequired,
};

export default BatchEdit;
